import Image from 'next/image'
import type { NestedCarouselCard } from '@components/ui/carousel/nested-cards-carousel/NestedCardsCarousel'
import { Container } from '@components/shared/container'
import { paths } from '@router'
import type {
  HomePageFundRecord,
  HomePageFundsClientPayload,
} from '@domain/landing/homePageFundPayload'
import { generatePath } from '@utils/path.utils'
import { createStrapiMediaUrl } from '@utils/strapi.utils'
import { CtaButtons } from './CtaButtons'
import { formatWlthGainPct, getFundEntryCurrentChange } from './entryCurrentValuation'
import { LandingHeroPreIPOCarouselClient } from './LandingHeroPreIPOCarouselClient'

type LandingHeroPreIPOCarouselProps = {
  homeFunds: HomePageFundsClientPayload
}

/** Homepage hero cards: hide parenthetical suffixes (e.g. "SpaceX (pre-xAI merger)" → "SpaceX"). */
const stripParentheticalFromFundName = (name: string) =>
  name.replace(/\s*\([^)]*\)/g, '').trim()

// Helper function to transform funds data to carousel format
export const transformFundsToCarouselData = (
  funds: HomePageFundRecord[],
): NestedCarouselCard[] => {
  return funds
    .filter((fund) => fund.heroSection?.promoteOnHomepageHero)
    .map((fund) => {
      const change = getFundEntryCurrentChange(fund)
      const href = fund.fundUrlId
        ? generatePath(paths.funds.exclusiveAccess.preIPOAccessDetails, {
            fundId: fund.fundUrlId,
          })
        : undefined

      return {
        title: stripParentheticalFromFundName(fund.fundName),
        image: createStrapiMediaUrl(fund.heroSection?.heroCoverImage),
        description: fund.heroSection?.heroTagline || '',
        fundUrlId: fund.fundUrlId,
        currentValuation: fund.communityFundCustomData?.currentValuation || '0',
        ...(change
          ? {
              gainPct: change.percent,
              footerMode: 'pct' as const,
            }
          : {}),
        ...(href ? { href } : {}),
      }
    })
}

// Server component that renders the carousel for SEO
export const LandingHeroPreIPOCarousel = ({
  homeFunds,
}: LandingHeroPreIPOCarouselProps) => {
  const carouselData = transformFundsToCarouselData(homeFunds.data)

  return (
    <div className="relative w-full mt-0 sm:mt-[55px]">
      <Container className="relative">
        <style>{`
          .hero-badge { width: 130px; height: 130px; }
          @media (min-width: 599px) { .hero-badge { width: 170px; height: 170px; } }
        `}</style>
        <Image
          src="/images/wlth/hero/badge.webp"
          alt="Pre-IPO Access Hero"
          width={170}
          height={170}
          priority
          sizes="(max-width: 598px) 130px, 170px"
          className="hero-badge absolute top-[24rem] md:top-44 -left-6 md:left-0"
        />
      </Container>

      <div className="min-h-[360px] mt-0 sm:-mt-22">
        {carouselData.length > 0 && (
          <div className="sr-only" aria-hidden="false">
            {carouselData.map((item, index) => (
              <article key={index} itemScope itemType="https://schema.org/InvestmentFund">
                <span itemProp="name" className="text-h2">
                  {item.title}
                </span>
                <p itemProp="description">{item.description}</p>
                <img
                  src={item.image}
                  alt={item.title}
                  itemProp="image"
                  width={290}
                  height={280}
                  loading="lazy"
                />
                <span itemProp="value">
                  {typeof item.gainPct === 'number' && Number.isFinite(item.gainPct)
                    ? formatWlthGainPct(item.gainPct)
                    : item.currentValuation}
                </span>
                {item.href ? (
                  <a href={item.href} itemProp="url">
                    {item.title}
                  </a>
                ) : null}
              </article>
            ))}
          </div>
        )}
        <LandingHeroPreIPOCarouselClient homeFunds={homeFunds} />
      </div>
      {/* TODO(Brand Gate): H1 copy pending Brand-Guide-Bot PASS before changing "Invest in unicorns before they go public". */}
      <h1 className="text-white text-6xl sm:text-8xl font-bold font-funnel text-center mt-14 sm:mt-10 px-[50px] sm:px-0">
        Invest in leading companies <br /> private and public
      </h1>
      <p className="text-white font-funnel text-xl font-normal text-center mt-11 px-[35px] sm:px-0">
        Share in the success of the companies shaping your world.
      </p>
      <CtaButtons />
    </div>
  )
}
